import { navigate } from '@reach/router';
import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../../api/useApiRequest';
import { PrimaryProgressButton, SecondaryButton } from '../../shared/Button';
import { Header3 } from '../../shared/Headers';
import { LoadingIndicator } from '../../shared/LoadingIndicator';
import { Modal, ModalActionButtons, ModalErrorBox, ModalHeader } from '../../shared/Modal';
import { colourGreen05 } from '../../styling/colours';
import { spacing05, spacing06 } from '../../styling/spacing';
import { projectUrls } from '../projects/projectUrls';

type Props = {
  projectId: number;
  designId: number;
  isOpen: boolean;
  closeModal: () => void;
  shouldRegenerate: boolean;
};

export const GenerateBillOfMaterialsForDesignModal = ({
  projectId,
  designId,
  isOpen,
  closeModal,
  shouldRegenerate,
}: Props) => {
  const generateBillOfMaterialsApiRequest = useApiRequest<undefined, GenerateBillOfMaterialsCommand>({
    httpMethod: 'post',
    endpointUrl: `projects/${projectId}/designs/${designId}/bill-of-materials`,
  });

  const { inProgress, error } = generateBillOfMaterialsApiRequest;

  useEffect(() => {
    if (isOpen && !shouldRegenerate) {
      sendRequest();
    }
  }, [isOpen, shouldRegenerate]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendRequest = () => {
    void generateBillOfMaterialsApiRequest.sendRequest({
      requestBody: {
        designId,
        shouldRegenerate,
      },
      onSuccess: () => navigate(projectUrls.editDesignBillOfMaterials(projectId, designId)),
    });
  };

  if (shouldRegenerate) {
    return (
      <Modal isOpen={isOpen}>
        <ModalHeader>Regenerate Bill of Materials</ModalHeader>
        <p>Are you sure you wish to regenerate the Bill of Materials for this Design?</p>
        <p>This will overwrite the existing Bill of Materials, including any updates that might have been made.</p>
        <p>This action cannot be undone.</p>
        {error && <ModalErrorBox error={error} />}
        <ModalActionButtons $align="right">
          <SecondaryButton onClick={closeModal} disabled={inProgress}>
            Cancel
          </SecondaryButton>
          <PrimaryProgressButton onClick={sendRequest} inProgress={inProgress}>
            Confirm
          </PrimaryProgressButton>
        </ModalActionButtons>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen}>
      {inProgress ? (
        <CenteredContainer>
          <Header3>Generating Bill of Materials...</Header3>
          <StyledLoadingIndicator size={10} colour={colourGreen05} />
        </CenteredContainer>
      ) : (
        <>
          {error && <ModalErrorBox error={error} />}
          <ModalActionButtons $align="right">
            <SecondaryButton onClick={closeModal} disabled={inProgress}>
              Cancel
            </SecondaryButton>
          </ModalActionButtons>
        </>
      )}
    </Modal>
  );
};

type GenerateBillOfMaterialsCommand = {
  designId: number;
  shouldRegenerate: boolean;
};

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: ${spacing06} 0;
`;

const StyledLoadingIndicator = styled(LoadingIndicator)`
  margin-top: ${spacing05};
`;
